import { useState, useCallback } from 'react'
import { env } from '@/config/env.ts'
import { getFriendlyTransactionError } from '@/utils/walletError.ts'
import { useContract } from './useContract.ts'
import { useWallet } from './useWallet.ts'
import type {
  VaultDecryptionInfo,
  ContractWriteResult,
} from '@/services/contract/contract.types.ts'

interface UseBeneficiaryState {
  assignedVaultIds: bigint[]
  loading: boolean
  error: string | null
}

interface UseBeneficiaryReturn extends UseBeneficiaryState {
  fetchAssignedVaults: () => Promise<void>
  canDecrypt: (vaultId: bigint) => Promise<boolean | null>
  getDecryptionInfo: (vaultId: bigint) => Promise<VaultDecryptionInfo | null>
  accessVault: (vaultId: bigint) => Promise<ContractWriteResult | null>
}

export function useBeneficiary(): UseBeneficiaryReturn {
  const contract = useContract()
  const { address } = useWallet()

  const [state, setState] = useState<UseBeneficiaryState>({
    assignedVaultIds: [],
    loading: false,
    error: null,
  })

  const setLoading = () =>
    setState((s) => ({ ...s, loading: true, error: null }))
  const setError = (error: string) =>
    setState((s) => ({ ...s, loading: false, error }))

  const fetchAssignedVaults = useCallback(async () => {
    setLoading()
    try {
      if (env.enableDebug && address) {
        console.log(
          '[useBeneficiary] fetchAssignedVaults: connected address =',
          address,
        )
      }
      const assignedVaultIds = await contract.getVaultsAssignedToMe(
        address ? (address as `0x${string}`) : undefined,
      )
      if (env.enableDebug) {
        console.log(
          '[useBeneficiary] getVaultsAssignedToMe() returned:',
          assignedVaultIds,
        )
        console.log('[useBeneficiary] count:', assignedVaultIds?.length ?? 0)
      }
      setState((s) => ({ ...s, assignedVaultIds, loading: false }))
    } catch (e) {
      if (env.enableDebug) {
        console.error('[useBeneficiary] fetchAssignedVaults error:', e)
      }
      setError(getFriendlyTransactionError(e))
    }
  }, [contract, address])

  const canDecrypt = useCallback(
    async (vaultId: bigint): Promise<boolean | null> => {
      if (!address) return null
      try {
        return await contract.canDecrypt(address as `0x${string}`, vaultId)
      } catch (e) {
        setError(getFriendlyTransactionError(e))
        return null
      }
    },
    [address, contract],
  )

  const getDecryptionInfo = useCallback(
    async (vaultId: bigint): Promise<VaultDecryptionInfo | null> => {
      setLoading()
      try {
        const info = await contract.getVaultDecryptionInfo(vaultId)
        setState((s) => ({ ...s, loading: false }))
        return info
      } catch (e) {
        setError(getFriendlyTransactionError(e))
        return null
      }
    },
    [contract],
  )

  const accessVault = useCallback(
    async (vaultId: bigint): Promise<ContractWriteResult | null> => {
      setLoading()
      try {
        const result = await contract.accessVault(vaultId)
        setState((s) => ({ ...s, loading: false }))
        return result
      } catch (e) {
        setError(getFriendlyTransactionError(e))
        return null
      }
    },
    [contract],
  )

  return {
    ...state,
    fetchAssignedVaults,
    canDecrypt,
    getDecryptionInfo,
    accessVault,
  }
}
